import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../../lib/api";
import { PublicLayout } from "../../components/PublicLayout";

export function LoginPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
      await qc.invalidateQueries({ queryKey: ["me"] });
      nav(params.get("next") ?? "/app");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) setError(t("common.error"));
      else setError(t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title={t("auth.loginTitle")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="label">{t("auth.email")}</label>
          <input type="email" required className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <label className="label">{t("auth.password")}</label>
          <input type="password" required className="input" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        {error && <p className="text-red-600 text-sm">{error}</p>}
        <button disabled={busy} className="btn-primary w-full">{t("auth.submitLogin")}</button>
        <div className="text-sm text-slate-600 flex justify-between">
          <Link to="/forgot-password" className="underline">{t("auth.forgot")}</Link>
          <Link to="/signup" className="underline">{t("auth.noAccount")}</Link>
        </div>
      </form>
    </AuthLayout>
  );
}

export function AuthLayout({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <PublicLayout variant="center">
      <div className="card w-full max-w-md p-7">
        <h1 className="font-display text-2xl font-medium text-[#1B0E16] mb-6">{title}</h1>
        {children}
      </div>
    </PublicLayout>
  );
}
